import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import cloud from "../../../assets/images/homepage/cloud.svg";
import cloudDark from "../../../assets/images/homepage/cloudDark.svg";
import orangeCloud from "assets/images/homepage/orange.svg";
import redCloud from "assets/images/homepage/red.svg";
// import storageStar from "../../../assets/images/homepage/storageStar.svg";
import "./storage.scss";
import useTheme from "hooks/observer";
import { signupContext } from "context/SignupContext";
import { sdk, ManageUser as MU, StoragePlan } from "@induz/sdk";

// Helpers for plan storage (bytes → GB and display string); kept in UI to avoid SDK build/export issues
function storageBytesToGb(bytes) {
  const n = typeof bytes === "number" ? bytes : (bytes?.toNumber?.() ?? 0);
  return n / 1024 ** 3;
}
function formatStorageBytes(bytes) {
  const n = typeof bytes === "number" ? bytes : (bytes?.toNumber?.() ?? 0);
  const gb = 1024 ** 3;
  const tb = 1024 ** 4;
  const pb = 1024 ** 5;
  if (n >= pb) return `${Math.round((n / pb) * 100) / 100} PB`;
  if (n >= tb) return `${Math.round((n / tb) * 100) / 100} TB`;
  return `${Math.round((n / gb) * 100) / 100} GB`;
}

function StorageProgress() {
  const theme = useTheme();

  const { setStorageBalance } = useContext(signupContext);

  // Retrieve stored values or set defaults (Basic = 100 GB; Pro/Ultra Pro from backend)
  const [memory, setMemory] = useState(() =>
    sessionStorage.getItem("memory")
      ? parseInt(sessionStorage.getItem("memory"), 10)
      : 100
  );

  // Always fetch from backend - no sessionStorage caching for these values
  const [percentage, setPercentage] = useState(0);
  const [usedStorage, setUsedStorage] = useState(0);
  
  // Use ref to store the latest limit so callback always uses current value
  const limitRef = useRef(memory);
  
  useEffect(() => {
    limitRef.current = memory;
  }, [memory]);
  
  const bytesToGB = useCallback((bytes, limitFromCallback) => {
    const bytesNum = typeof bytes === "number" ? bytes : (bytes?.toNumber?.() ?? 0);
    
    // Always use the current limit from state/ref (fetched from plan API)
    // Don't trust callback's limit as it might be default 100
    const resolvedLimit = limitRef.current || 
      (sessionStorage.getItem("memory") ? parseInt(sessionStorage.getItem("memory"), 10) : 100);

    const total = resolvedLimit * 1024 ** 3;
    const percentage = Math.round((bytesNum * 100) / total);
    const usedGB = Math.round((bytesNum / (1024 ** 3)) * 100) / 100; // Convert bytes to GB and round to 2 decimal places
    
    // Update state directly from backend - no sessionStorage caching
    setUsedStorage(usedGB);
    setPercentage(percentage);
    setStorageBalance(percentage);
  }, [setStorageBalance]);

  // Display limit from plan storage (GB / TB / PB)
  const formatLimitDisplay = (limitGb) =>
    formatStorageBytes((limitGb || 0) * 1024 ** 3);

  const getStorageData = useCallback(async (forceRefresh = false) => {
    try {
      // Always fetch fresh data from backend - don't rely on sessionStorage for limit
      const userInfo = await MU.getUserDetails(undefined, forceRefresh);
      const planId = userInfo?.getPlan?.();
      let limitInGb = undefined;
      
      if (typeof planId === "number") {
        const storagePlanObj = new StoragePlan();
        storagePlanObj.setPlanId(planId);
        const plan = await storagePlanObj.getPlan();
        const storageBytes = plan.getStorage();
        if (storageBytes != null) {
          limitInGb = storageBytesToGb(storageBytes);
          // Update sessionStorage and state with fresh limit from backend
          sessionStorage.setItem("memory", limitInGb.toString());
          setMemory(limitInGb);
          limitRef.current = limitInGb; // Update ref immediately
        }
      } else {
        // Fallback to sessionStorage if plan fetch fails
        limitInGb = sessionStorage.getItem("memory")
          ? parseInt(sessionStorage.getItem("memory"), 10)
          : 100;
        limitRef.current = limitInGb;
      }

      // Fetch current storage size directly from backend API
      const sizeRaw = await MU.getStorageSize();
      const size = typeof sizeRaw === "number" ? sizeRaw : (sizeRaw?.toNumber?.() ?? 0);
      
      // Calculate values directly here to ensure both instances use identical calculation
      const bytesNum = typeof size === "number" ? size : (size?.toNumber?.() ?? 0);
      const resolvedLimit = limitInGb || limitRef.current || 
        (sessionStorage.getItem("memory") ? parseInt(sessionStorage.getItem("memory"), 10) : 100);
      
      const total = resolvedLimit * 1024 ** 3;
      const percentage = Math.round((bytesNum * 100) / total);
      const usedGB = Math.round((bytesNum / (1024 ** 3)) * 100) / 100; // Round to 2 decimal places
      
      // Update state - both instances will get identical values
      setUsedStorage(usedGB);
      setPercentage(percentage);
      setStorageBalance(percentage);
    } catch (error) {
      console.error("Error fetching storage data:", error);
    }
  }, [setStorageBalance]); // Only setStorageBalance changes, but it's from context so stable
  
  // Register a SINGLE global callback that dispatches event for all instances
  // SDK only allows one callback, so we use a shared handler
  useEffect(() => {
    // Only register once globally (first instance to mount)
    if (!window._storageCallbackRegistered) {
      const globalStorageCallback = (bytes, limit) => {
        // Dispatch event so ALL StorageProgress instances fetch fresh data
        // This ensures both instances get the same values from backend
        window.dispatchEvent(new CustomEvent("storageSizeUpdate"));
      };
      sdk.registerCallback("storagePercent", globalStorageCallback);
      window._storageCallbackRegistered = true;
    }
    
    // Listen for the global event - fetch fresh data from backend to ensure sync
    const onStorageSizeUpdate = () => {
      // Fetch fresh data from backend so both instances get identical values
      getStorageData(true);
    };
    
    window.addEventListener("storageSizeUpdate", onStorageSizeUpdate);
    return () => window.removeEventListener("storageSizeUpdate", onStorageSizeUpdate);
  }, [getStorageData]); // Include getStorageData in deps

  // Fetch storage data on mount - always from backend
  // Also refetch periodically to ensure limit stays in sync (in case plan was upgraded)
  useEffect(() => {
    if (!sessionStorage.getItem("memory")) {
      sessionStorage.setItem("memory", "100"); // Default memory if not set
    }
    
    // Initial fetch
    getStorageData();
    
    // Also refetch every 10 seconds to catch plan upgrades or limit changes
    // This ensures both instances stay in sync even if one misses an event
    const intervalId = setInterval(() => {
      getStorageData(true); // Force refresh to get latest limit
    }, 10000); // 10 seconds - more frequent to catch updates faster
    
    return () => clearInterval(intervalId);
  }, []); // Only run on mount

  // Refetch storage limit as soon as plan is upgraded (no refresh/re-login needed)
  useEffect(() => {
    const onPlanUpgraded = () => getStorageData(true);
    window.addEventListener("planUpgraded", onPlanUpgraded);
    return () => window.removeEventListener("planUpgraded", onPlanUpgraded);
  }, []);

  // Listen for storage updates when files are uploaded/deleted
  // Both instances will fetch fresh data from backend when this event fires
  useEffect(() => {
    const onStorageUpdated = () => {
      // Small delay to ensure backend has updated storage
      // Use requestAnimationFrame to ensure both instances fetch in the same frame
      requestAnimationFrame(() => {
        setTimeout(() => {
          getStorageData(true); // Force refresh from backend
        }, 500);
      });
    };
    window.addEventListener("storageUpdated", onStorageUpdated);
    return () => window.removeEventListener("storageUpdated", onStorageUpdated);
  }, [getStorageData]);

  return (
    <div className="Storage">
      <div className="StorageWrapper">
        <div className="percent">
          {percentage < 80 && (
            <img src={theme === "light" ? cloud : cloudDark} alt="cloud" />
          )}
          {percentage >= 80 && percentage < 100 && (
            <img src={orangeCloud} alt="cloud" />
          )}
          {percentage >= 100 && <img src={redCloud} alt="cloud" />}
          <p className="text">Storage ({percentage}% Used)</p>
        </div>
        <div className="processBar">
          <div
            className={`progressBarFill ${percentage < 80
              ? "defaultColor"
              : percentage >= 80 && percentage < 100
                ? "orange"
                : "red"
              }`}
            style={{ width: `${percentage}%` }}
            role="progressbar"
            aria-valuenow={percentage}
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
        <div className="update">
          {usedStorage} GB of {formatLimitDisplay(memory)} used
        </div>
      </div>

      {/* <div
        className="StoragePro"
        onClick={() => {
          // Force clear any existing activeTab value first
          sessionStorage.removeItem("activeTab");

          // Set the active tab to Billing (index 3) in sessionStorage
          // Use setTimeout to ensure this happens after the removal
          setTimeout(() => {
            // Set the active tab to Billing (index 3)
            sessionStorage.setItem("activeTab", "3");

            // Set a flag to automatically open the Change Plan component
            sessionStorage.setItem("openChangePlanFromBilling", "true");

            // Check if settings are already open
            const isSettingsOpen =
              document.querySelector(".OrganizationSetting") ||
              document.querySelector(".IndividualSetting") ||
              document.querySelector(".EmployeeSetting");

            if (isSettingsOpen) {
              // If settings are already open, dispatch a custom event to update the active tab
              const event = new CustomEvent("updateSettingsTab");
              window.dispatchEvent(event);
            } else {
              // If settings are not open, open them
              const event = new CustomEvent("openSettings");
              window.dispatchEvent(event);
            }
          }, 0);
        }}
      >
        <img src={storageStar} alt="star" />
        <p>Upgrade to Pro</p>
      </div> */}
    </div>
  );
}

export default StorageProgress;
